// Pure helpers for the datetime cell editor. Kept in their own module so
// the unit tests can import them without dragging in the React + radix-ui
// component graph that cell-editor.tsx pulls.

// Splits an ISO-ish datetime string into the YYYY-MM-DDTHH:MM:SS base and
// (if present) the fractional-seconds digit run. Walks the chars after a
// "." at position 19 until the first non-digit so a trailing offset / "Z"
// is dropped automatically.
export interface IDatetimeParts {
  base: string;
  fractional: string | null;
}

export const splitDatetimeFraction = (raw: string): IDatetimeParts => {
  if (raw.length < 20 || raw[19] !== ".") {
    return { base: raw.slice(0, 19), fractional: null };
  }
  let i: number = 20;
  while (i < raw.length && raw[i] >= "0" && raw[i] <= "9") {
    i++;
  }
  return { base: raw.slice(0, 19), fractional: raw.slice(20, i) };
};

// Format a datetime for the <input type="datetime-local"> value. When the
// source carries fractional seconds, normalise to exactly 3 digits — the
// HTML control with step=0.001 expects "YYYY-MM-DDTHH:MM:SS.SSS" precisely;
// shorter runs (".7", ".78") render as empty in some browsers, longer runs
// (microseconds / nanoseconds) get rejected. Whole-second values stay 19
// chars so the seconds-only picker still shows up.
export const formatDatetimeForLocalInput = (raw: string): string => {
  const parts: IDatetimeParts = splitDatetimeFraction(raw);
  if (parts.fractional === null) {
    return parts.base;
  }
  const padded: string = (parts.fractional + "000").slice(0, 3);
  return `${parts.base}.${padded}`;
};

export const datetimeHasFractionalSeconds = (raw: string): boolean => {
  return splitDatetimeFraction(raw).fractional !== null;
};

// Format a DateTimeOffset value for the read-only cell display. The
// raw wire format is ISO-8601 with an offset (`2026-05-26T14:30:00+00:00`
// or `2026-05-26T14:30:00Z`), which is faithful but ugly to scan in a
// dense grid. Reformat to a `YYYY-MM-DD HH:MM:SS UTC` shape:
//
//   - Replace the `T` separator with a space — easier to read.
//   - Drop the trailing offset / `Z` — the column is always stored
//     in UTC, so the suffix becomes a single explicit `UTC` tag.
//   - Keep fractional seconds when present.
//
// We don't try to detect non-UTC offsets and reformat them — by the
// table editor's contract every DateTimeOffset save goes through with
// `+00:00`, so anything else has to have been written by another tool
// and would be misleading to silently translate. In that case we leave
// the string unchanged so the operator sees exactly what's stored.
export const formatDatetimeOffsetForDisplay = (raw: string): string => {
  if (raw.length === 0) {
    return raw;
  }
  // Strip a trailing `Z` or `±HH:MM` / `±HHMM` / `±HH` offset. The
  // anchored regex makes sure we don't chew into the body.
  const utcSuffix: RegExp = /(Z|[+-]00:?00|[+-]00)$/;
  if (!utcSuffix.test(raw)) {
    return raw;
  }
  const body: string = raw.replace(utcSuffix, "");
  // Swap the ISO `T` separator for a readable space; cheaper than a
  // full reformat and preserves fractional seconds untouched.
  const swapped: string = body.length >= 11 && body[10] === "T" ? `${body.slice(0, 10)} ${body.slice(11)}` : body;
  return `${swapped} UTC`;
};
